export interface UserNameClaims {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
}

const LOCALE = 'es-MX';
const WORD_SEPARATORS = /[._\-\s]+/;

const capitalize = (word: string): string =>
  word.charAt(0).toLocaleUpperCase(LOCALE) + word.slice(1).toLocaleLowerCase(LOCALE);

const toWords = (value: string | undefined): string[] =>
  (value ?? '').split(WORD_SEPARATORS).filter(Boolean).map(capitalize);

const isRepeatedBlock = (words: string[], start: number, size: number): boolean =>
  words.slice(start, start + size).join(' ') === words.slice(start + size, start + 2 * size).join(' ');

/** Quita bloques de palabras repetidos de forma consecutiva: "Hector Gomez Hector Gomez" → "Hector Gomez". */
const removeConsecutiveRepeats = (words: string[]): string[] => {
  for (let size = Math.floor(words.length / 2); size > 0; size--) {
    for (let start = 0; start + 2 * size <= words.length; start++) {
      if (isRepeatedBlock(words, start, size)) {
        return removeConsecutiveRepeats([...words.slice(0, start + size), ...words.slice(start + 2 * size)]);
      }
    }
  }
  return words;
};

const toDisplayName = (words: string[]): string => removeConsecutiveRepeats(words).join(' ');

/**
 * Nombre a mostrar del usuario a partir de los claims del JWT. Los usuarios creados
 * desde el BFF llegan con `given_name` y `family_name` iguales a la parte local del
 * correo (p. ej. "hector.gomez"), así que cada parte se normaliza (sin puntos, guiones
 * ni guiones bajos, con mayúscula inicial) y no se repite cuando coinciden.
 */
export const getUserDisplayName = ({ name, given_name, family_name, email }: UserNameClaims): string => {
  const givenName = toDisplayName(toWords(given_name));
  const familyName = toDisplayName(toWords(family_name));

  if (givenName || familyName) {
    return toDisplayName(toWords(givenName === familyName ? givenName : `${givenName} ${familyName}`));
  }

  return toDisplayName(toWords(name)) || toDisplayName(toWords(email?.split('@')[0]));
};
